import { FootPrintPadHoleType, GENESIS_TERMINAL_PART_UID, LayoutRules, PcbNodeTypes, PcbViaThermalReliefTypes, matchLayerIdentifier, pcbLayoutNestedNodeDelimiter, } from "@buildwithflux/models";
import { isEmpty } from "lodash";
import { Euler, Matrix4, Vector3 } from "three";
import { AutoLayoutHelpers } from "../auto_layout";
import { PcbLayoutNodeDataIntegrityError } from "./error";
function validatePcbNodePredicates(predicates, node) {
    return predicates.reduce((prev, current) => {
        return prev && current(node);
    }, true);
}
export class PcbNodeHelper {
    /**
     * Mutable hook for configuring an error handler for the helper - should be configured before using the helper
     * in an application context. It'd be better if the helper took a runtime dependency on a Logger instance, and
     * used that to log details.
     *
     * In the frontend, replace the implementation with FluxLogger.captureError
     */
    static setOnErrorHandler(handler) {
        this.onError = handler;
    }
    /**
     * Current Error handler implementation
     *
     * Implementation will be mutated away to something else, by setOnErrorHandler
     */
    static onError = (error) => {
        // eslint-disable-next-line no-console
        console.error(error);
    };
    /**
     * A PcbLayoutNode satisfying the following conditions is said to be associated with a Terminal:
     * 1. It is a Pad
     * 2. There is an associated element in the doc, and that element is a Terminal
     *
     * NOTE: We are passing in `elements` here because we want this file/module to be completely independent.
     * Hence, instead of for example grabbing elements data from redux (which creates dependency to store.ts),
     * we should just pass in the elements object.
     * @param nodeData
     * @returns
     */
    static isTerminal(elements, node) {
        if (!node)
            return false;
        if (!node.uid.includes(pcbLayoutNestedNodeDelimiter)) {
            const elementData = elements?.[node.uid];
            return (node.type === PcbNodeTypes.pad &&
                !!elementData &&
                !!elementData.part_uid &&
                elementData.part_uid === GENESIS_TERMINAL_PART_UID);
        }
        else {
            let elementData;
            const uids = node.uid.split(pcbLayoutNestedNodeDelimiter);
            let elementsMap = elements;
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            uids.forEach((uid) => {
                if (elementsMap && elementsMap[uid]) {
                    elementData = elementsMap[uid];
                }
                elementsMap = elementData?.part_version_data_cache?.extracted_element_cache;
            });
            return (node.type === PcbNodeTypes.pad &&
                !!elementData &&
                !!elementData.part_version_data_cache?.part_uid &&
                elementData.part_version_data_cache?.part_uid === GENESIS_TERMINAL_PART_UID);
        }
    }
    static getNodeOfType(pcbLayoutNodes, nodeUid, type) {
        // NOTE: the passed in `nodeUid` could be empty or undefined for cases such as `topLevelLayoutUid`,
        // where it could be undefined, or got fallback to empty string
        if (nodeUid === "" || nodeUid === undefined)
            return undefined;
        const node = pcbLayoutNodes[nodeUid];
        if (!node) {
            // Capture it in Sentry, also throw here to stop running the app
            // because if a node is undefined, something is really wrong
            const error = new PcbLayoutNodeDataIntegrityError(`Cant find node data for "${nodeUid}" in getNodeOfType`);
            if (!isEmpty(pcbLayoutNodes)) {
                this.onError(error);
            }
            throw error;
        }
        if (node.type === type) {
            return node;
        }
        return undefined;
    }
    static isNodeNotRoot(node) {
        return Boolean(node) && node?.type !== PcbNodeTypes.root;
    }
    static findNodeOfType(pcbLayoutNodes, type, otherPredicates) {
        return pcbLayoutNodes.find((node) => node.type === type && (!otherPredicates || validatePcbNodePredicates(otherPredicates, node)));
    }
    static filterNodesOfType(pcbLayoutNodes, type, otherPredicates) {
        return pcbLayoutNodes.filter((node) => node.type === type && (!otherPredicates || validatePcbNodePredicates(otherPredicates, node)));
    }
    /**
     * Check if a node is under the element's sublayout.
     *
     * Here we say a node is under the element's sublayout when that node comes from the element's part version.
     * Any node customizely added to the element by the user is NOT considered a node under element's sublayout.
     *
     * A node that's under the element's sublayout will have this prefix: `<elementUid>__`
     *
     * @param nodeUid - uid of the node we are checking
     * @param elementUid - uid of the element
     * @returns
     */
    static isNodeInElementSubLayout(nodeUid, elementUid) {
        return nodeUid.startsWith(`${elementUid}${pcbLayoutNestedNodeDelimiter}`);
    }
    /**
     * NOTE: this method also checks for the presense of a footprint because a
     * node can have both! In the context of the pick-and-place, we want the
     * footprint to "win" over the sublayout because it does have a defined
     * physical position on the final board.
     */
    static isNodeExclusiveSubLayoutContainer(node, nodes) {
        const hasLayout = node.childUids.some((childUid) => nodes[childUid]?.type === PcbNodeTypes.layout);
        const hasNonEmptyFootprint = node.childUids.some((childUid) => nodes[childUid]?.type === PcbNodeTypes.footprint && nodes[childUid]?.childUids.length);
        return node.type === PcbNodeTypes.element && hasLayout && !hasNonEmptyFootprint;
    }
    /**
     * Check if a node is under any sublayout. We construct uid for sublayout node
     * with the following format: <parentUid>__<nodeUid>, if there are multiple nested levels,
     * we keep stitching them with `pcbLayoutNestedNodeDelimiter`. Hence, as long as a nodeUid
     * contains `pcbLayoutNestedNodeDelimiter`, we say that its under a sublayout
     *
     * The difference between this method and `isNodeInElementSubLayout` is that this
     * method only care if a node is under _any_ sublayout, not specifict to any
     * part
     *
     * @param nodeUid - uid of the node we are checking
     * @returns True if this node is under a sub-layout
     */
    static isNodeUnderSubLayout(nodeUid) {
        return nodeUid.includes(pcbLayoutNestedNodeDelimiter);
    }
    /**
     * Construct an uid for a sublayout node.
     *
     * We construct it by <parentUid>__<nodeUid>, we use this format to not only indicate
     * that this node is under a sublayout, but also allow "scoping" the sub layout node
     * under its ancestors' context
     *
     * @param {string} parentUid - a prefix string containing all parent nested parts
     * @param {string} nodeUid - the original uid of this node when its defined in the part
     *
     * @returns the result node uid for sub layout
     */
    static constructNodeUidForSubLayout(parentUid, nodeUid) {
        return `${parentUid}${pcbLayoutNestedNodeDelimiter}${nodeUid}`;
    }
    /**
     * Check if a node is system node. A system node cannot be deleted by user
     *
     *
     * @param {AnyPcbNode} node - node to be checked
     * @returns {boolean} whether or not the node is a system node
     */
    static isSystemNode(node) {
        return (node.type === PcbNodeTypes.container || node.type === PcbNodeTypes.net || node.type === PcbNodeTypes.element);
    }
    /**
     * Determines if a node is a baked node
     */
    static isBakedNode(node) {
        // NOTE: `"bakedRules" in node` is truthy even when its value is undefined, so need an extra null check here
        return node ? "bakedRules" in node && Boolean(node.bakedRules) : false;
    }
    /**
     * Returns the node if it is a baked node, as a baked node, otherwise returns undefined
     */
    static asBakedNode(node) {
        return PcbNodeHelper.isBakedNode(node) ? node : undefined;
    }
    /**
     * Type predicate to check if a node is virtual
     *
     * The key to decide if a node is virtual is whether or not those nodes are generated/derived in run-time
     *
     * Some more notes to distinguish a user owned node and a virtual node:
     * - User owned and virtual when it is a virtual node generated by the project the user is currently editing
     *   - Example: All the copper fills and stitching via nodes under my own project
     * - User owned and non-virtual - this is apparent, any node I created under my project
     * - Not user owned and virtual - all the virtual nodes in the sub-layouts I'm consuming in my project
     * - Not user owned and non-virtual - all the non-virtual nodes in the sub-layouts I'm consuming in my project.
     */
    static isVirtualNode(node) {
        if (!node) {
            return false;
        }
        // NOTE: `"virtualNodeInfo" in node` is truthy even when its value is undefined, so need an extra null check here
        return (this.isBakedNode(node) &&
            // We cannot use Boolean(node.virtualNodeInfo) directly here, because "virtualNodeInfo" only exist in type PcbVirtualNode.
            // So we need to check "virtualNodeInfo" in node but that is not sufficient to say virtualNodeInfo is truthy
            "virtualNodeInfo" in node &&
            Boolean(node.virtualNodeInfo));
    }
    /**
     * Check if a node has auto positioning enabled.
     *
     * A node is considered auto-positioned if it has active baked rules with position
     * settings that enable automatic X or Y positioning.
     */
    static isAutoPositioned(node) {
        return (node.bakedRules?.active &&
            "position" in node.bakedRules &&
            (node.bakedRules?.position.autoX || node.bakedRules?.position.autoY));
    }
    /**
     * Check if a node is a virtual node, and return the virtual node info if it is
     *
     * @param node - node to be checked
     * @returns {IVirtualNodeInfo | undefined} - virtual node info if the node is virtual, undefined otherwise
     */
    static getVirtualNodeInfo(node) {
        if (this.isVirtualNode(node)) {
            return node.virtualNodeInfo;
        }
        return undefined;
    }
    static isAutoLayoutNode(node) {
        const hasPrefix = node.uid.startsWith(`${AutoLayoutHelpers.autoLayoutNodeUidPrefix}.`);
        if (!hasPrefix) {
            return false;
        }
        const isVirtualNode = PcbNodeHelper.isVirtualNode(node);
        if (!isVirtualNode) {
            return false;
        }
        return node.virtualNodeInfo.originRule === "autoLayout";
    }
    /**
     * Convert the passed-in position to be relative to the given parent node, if that node exists.
     *
     * @param scenePosition The position we want to convert, relative to scene.
     * @param referenceNode Uid of the parent node that position should be relative to ("frame of reference")
     * @returns the converted position in parent space
     */
    static scenePositionToNodeSpace(scenePosition, referenceNode) {
        const relativePosition = new Vector3(scenePosition.x, scenePosition.y, 0);
        if (this.isNodeNotRoot(referenceNode)) {
            const bakedRules = referenceNode.bakedRules;
            const refNodePosition = bakedRules.positionRelativeToDocument;
            const refNodeRotation = bakedRules.rotationRelativeToDocument;
            const tempMatrix = new Matrix4();
            tempMatrix.makeRotationFromEuler(new Euler(refNodeRotation.x, refNodeRotation.y, refNodeRotation.z));
            tempMatrix.setPosition(refNodePosition.x, refNodePosition.y, refNodePosition.z);
            tempMatrix.invert();
            relativePosition.applyMatrix4(tempMatrix);
        }
        return { x: relativePosition.x, y: relativePosition.y };
    }
    static layoutPositionToNodeSpace(layoutPosition, referenceNode) {
        const relativePosition = new Vector3(layoutPosition.x, layoutPosition.y, 0);
        if (this.isNodeNotRoot(referenceNode)) {
            const bakedRules = referenceNode.bakedRules;
            const refNodePosition = bakedRules.positionRelativeToRootLayout;
            const refNodeRotation = bakedRules.rotationRelativeToRootLayout;
            const tempMatrix = new Matrix4();
            tempMatrix.makeRotationFromEuler(new Euler(refNodeRotation?.x ?? 0, refNodeRotation?.y ?? 0, refNodeRotation?.z ?? 0));
            tempMatrix.setPosition(refNodePosition?.x ?? 0, refNodePosition?.y ?? 0, refNodePosition?.z ?? 0);
            tempMatrix.invert();
            relativePosition.applyMatrix4(tempMatrix);
        }
        return { x: relativePosition.x, y: relativePosition.y };
    }
    /**
     * Checks whether or not a list of pcbLayoutNodes contain element/terminal nodes
     *
     * We say that a list pcbLayoutNodes contain element/terminal nodes when
     * a. Any of those nodes is associated with an element/terminal
     * b. Any of descendants of the nodes is associated with an element/terminal
     *   - For example, if you passed a `Components` node which contains elements under it, this method
     *     should return true
     *
     * @param {string[]} nodeUids - uids of pcbLayoutNodes being checked
     * @param {PcbNodesMap<AnyPcbNode>} allNodes - all existing nodes in app, we need this to check tree branches. Pass `pcbLayoutNodes` from `documentService.snapshot()` because we need `childUids` there
     * @param {IElementsMap} allElements - all existing elements, need this to perform `isTerminal` check
     */
    static nodesContainElementsOrTerminals(nodeUids, allNodes, allElements) {
        const doesTreeBranchContainElementsOrTerminals = (nodeUid) => {
            const node = allNodes[nodeUid];
            if (!node) {
                return false;
            }
            // If current node is element/terminal, return true
            if (node && (node.type === PcbNodeTypes.element || (allElements && this.isTerminal(allElements, node)))) {
                return true;
            }
            // Then, iterate through this node's descendants recursively, and return true _immediately_
            // if any of them is element/terminal
            if (node.childUids && node.childUids.length > 0) {
                return node.childUids.some((childUid) => doesTreeBranchContainElementsOrTerminals(childUid));
            }
            // Otherwise, it is safe to say that there's no element/terminal involved in the selection
            return false;
        };
        return nodeUids.some((nodeUid) => doesTreeBranchContainElementsOrTerminals(nodeUid));
    }
    /**
     * Returns true if a node is suported by rule. (only need to check node.type)
     *
     * This will check the rule config to see if the given node's type is in
     * the rule's `unsupportedNodeTypes` and `supportedNodeTypes`
     */
    static supportedByRule(nodeType, ruleName) {
        const rule = LayoutRules[ruleName];
        return (!rule.unsupportedNodeTypes?.includes(nodeType) ||
            Boolean(rule.supportedNodeTypes && rule.supportedNodeTypes.includes(nodeType)));
    }
    /**
     * NOTE: Originally created for fill island removal.
     * TODO: change typing to be more generic to be consistent with other methods
     */
    static isNodeOnNetConnectedLayers(node, netOrPolygon) {
        // HACK: This is a temporary fix for the issue where an orphaned net
        // node is not baked
        const netConnectLayers = new Set(netOrPolygon.bakedRules?.connectedLayers ?? []);
        const nodeConnectLayers = new Set(node.bakedRules?.connectedLayers ?? []);
        if (netConnectLayers.size === 0 || nodeConnectLayers.size === 0) {
            return false;
        }
        if (netConnectLayers.has("All") || nodeConnectLayers.has("All")) {
            return true;
        }
        for (const netConnectLayer of netConnectLayers) {
            if (nodeConnectLayers.has(netConnectLayer)) {
                return true;
            }
        }
        return false;
    }
    /**
     * NOTE: Originally created for fills.
     */
    static isNodeConnectedToThisLayer(updatedNode, layer) {
        if (updatedNode.type !== PcbNodeTypes.pad) {
            return false;
        }
        // Checking the node is baked was determined to be necessary to avoid runtime errors that occurred
        // during testing of the "orbit agent"
        if (!PcbNodeHelper.isBakedNode(updatedNode)) {
            return false;
        }
        const bakedRules = updatedNode.bakedRules;
        switch (bakedRules.hole?.holeType) {
            case FootPrintPadHoleType.platedThroughHole: {
                const connectedLayers = bakedRules.connectedLayers ?? [];
                return connectedLayers.some((connectedLayer) => connectedLayer === "All" || matchLayerIdentifier(layer, connectedLayer));
            }
            case FootPrintPadHoleType.SurfaceMountDevice: {
                return matchLayerIdentifier(layer, bakedRules.layer);
            }
            default: {
                return false;
            }
        }
    }
    static nodeHasThermalRelief(updatedNode, netUid, layer) {
        if (updatedNode.type !== PcbNodeTypes.pad) {
            return false;
        }
        if (!PcbNodeHelper.isBakedNode(updatedNode)) {
            return false;
        }
        const bakedRules = updatedNode.bakedRules;
        return (bakedRules.thermalReliefType === PcbViaThermalReliefTypes.reliefConnect &&
            bakedRules.effectiveNetId === netUid &&
            PcbNodeHelper.isNodeConnectedToThisLayer(updatedNode, layer));
    }
    static isStitchingNode(node) {
        if (!node?.bakedRules?.active) {
            // Note this condition implicitly ensures node is defined. node being undefined was the source
            // of runtime exceptions caused by subsequent access to node.parentUid
            return false;
        }
        return (PcbNodeHelper.isVirtualNode(node) &&
            node.virtualNodeInfo.originRule === "fill" &&
            node.type === PcbNodeTypes.via);
    }
    static isStitchingNodeFromPolygon(node, allNodes) {
        return (PcbNodeHelper.isStitchingNode(node) &&
            allNodes[node.virtualNodeInfo.originNodeUid]?.type === PcbNodeTypes.polygon);
    }
    static stitchingShouldAvoidNode(node, fillParentNode) {
        const isCopperDisplacing = PcbNodeHelper.isCopperNodeType(node) || node.type === PcbNodeTypes.zone;
        // Fill nodes are copper, but a fill doesn't avoid itself (and other fills on same layer is a more complex future feature)
        const isFill = node.type === PcbNodeTypes.fill;
        // It's ok to put stitching vias over traces if they are on the same net
        const fillNetUid = fillParentNode.type === PcbNodeTypes.net ? fillParentNode.uid : fillParentNode.bakedRules.effectiveNetId;
        const isTraceSameNet = node.type === PcbNodeTypes.routeSegment && node.bakedRules.effectiveNetId === fillNetUid;
        const isActive = node.bakedRules ? node.bakedRules.active : false;
        return !isTraceSameNet && isCopperDisplacing && !isFill && isActive && !PcbNodeHelper.isStitchingNode(node);
    }
    static isCopperNodeType(node) {
        const copperNodeTypes = [PcbNodeTypes.pad, PcbNodeTypes.routeSegment, PcbNodeTypes.via, PcbNodeTypes.fill];
        return copperNodeTypes.includes(node.type);
    }
    /**
     * Check whether the given node is a virtual node under a smart via
     */
    static isNodeUnderSmartVia(node) {
        if (!node)
            return false;
        if ("bakedRules" in node && !node.bakedRules?.active) {
            // Note this condition implicitly ensures node is defined. node being undefined was the source
            // of runtime exceptions caused by subsequent access to node.parentUid
            return false;
        }
        return PcbNodeHelper.isVirtualNode(node) && node.virtualNodeInfo.originRule === "smartVia";
    }
}
//# sourceMappingURL=pcbNodeHelper.js.map